/**
 * Admin Team Management Panel
 * Allows admin to manage auction teams, owners, and wallets
 */
import { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../../config/axios';
import { toast } from 'sonner';
import { formatBYC, getErrorMessage } from './useAuction';

export default function TeamManagementPanel({ eventId, config, onTeamsChange }) {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreditModal, setShowCreditModal] = useState(null);
  const [showDebitModal, setShowDebitModal] = useState(null);

  // Current user — needed to unlock post-close wallet adjustments for Super Admin
  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  })();
  const isSuperAdmin = !!(currentUser && (currentUser.role === 'super_admin' || (currentUser.roles || []).includes('super_admin')));

  const canEdit = config?.auction_status === 'DRAFT' || config?.auction_status === 'CONFIGURED';
  // Admins can credit/debit only while auction is active. Super Admins can
  // ALWAYS credit/debit (including post-close adjustments) — audited separately.
  const canCredit = canEdit || config?.auction_status === 'LIVE' || isSuperAdmin;
  const isPostCloseOverride = isSuperAdmin && !canEdit && config?.auction_status !== 'LIVE';

  const fetchTeams = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/v1/events/${eventId}/auction/teams`);
      setTeams(res.data);
    } catch (err) {
      toast.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3AAFA9]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-management-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-bold text-lg">Teams ({teams.length}/{config?.total_teams || 0})</h3>
          <p className="text-sm text-gray-500">Team wallets for the auction</p>
        </div>
      </div>
      <p className="text-xs text-gray-500 bg-[#3AAFA9]/5 rounded-lg px-3 py-2" data-testid="teams-managed-in-teams-tab">
        <i className="ph-bold ph-info mr-1"></i>
        Teams and their owners are created and linked in the <strong>Teams</strong> tab. Wallets are set up automatically.
      </p>

      {/* Team Cards Grid */}
      {teams.length === 0 ? (
        <div className="bg-white rounded-xl p-8 border border-gray-100 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <i className="ph-bold ph-users-three text-2xl text-gray-400"></i>
          </div>
          <p className="text-gray-500">No teams yet — create them in the Teams tab.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {teams.map(team => (
            <TeamCard
              key={team.id}
              team={team}
              eventId={eventId}
              canEdit={canEdit}
              canCredit={canCredit}
              isSuperAdmin={isSuperAdmin}
              isPostCloseOverride={isPostCloseOverride}
              squadSize={config?.squad_size}
              onCreditWallet={() => setShowCreditModal(team)}
              onDebitWallet={(t) => setShowDebitModal(t)}
              onRefresh={() => { fetchTeams(); onTeamsChange?.(); }}
            />
          ))}
        </div>
      )}

      {/* Credit Wallet Modal */}
      {showCreditModal && (
        <CreditWalletModal
          eventId={eventId}
          team={showCreditModal}
          isPostCloseOverride={isPostCloseOverride}
          onClose={() => setShowCreditModal(null)}
          onCredited={() => { fetchTeams(); onTeamsChange?.(); }}
        />
      )}

      {/* Debit Wallet Modal */}
      {showDebitModal && (
        <DebitWalletModal
          eventId={eventId}
          team={showDebitModal}
          isPostCloseOverride={isPostCloseOverride}
          onClose={() => setShowDebitModal(null)}
          onDebited={() => { fetchTeams(); onTeamsChange?.(); }}
        />
      )}
    </div>
  );
}

// Team Card Component
function TeamCard({ team, eventId, canEdit, canCredit, isSuperAdmin, isPostCloseOverride, squadSize, onCreditWallet, onDebitWallet, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [showRecalc, setShowRecalc] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const isAdmin = canEdit || isSuperAdmin;

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so same file can be re-picked
    if (!file) return;
    // Accept by mime-type OR by extension — some browsers report empty
    // mime-type for .heic/.heif files from iPhone.
    const name = (file.name || '').toLowerCase();
    const looksLikeImage =
      file.type.startsWith('image/') ||
      /\.(heic|heif|jpe?g|png|gif|webp|bmp|svg)$/i.test(name);
    if (!looksLikeImage) {
      toast.error('Please pick an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5 MB');
      return;
    }
    try {
      setUploadingLogo(true);

      // 1) Ask backend for a signed Cloudinary upload signature
      const folder = 'registrations/team_logos';
      const sigRes = await axiosInstance.get(
        `/cloudinary/signature?resource_type=image&folder=${folder}`
      );
      const sig = sigRes.data;

      // 2) POST the file directly to Cloudinary with the signed params
      const fd = new FormData();
      fd.append('file', file);
      fd.append('api_key', sig.api_key);
      fd.append('timestamp', sig.timestamp);
      fd.append('signature', sig.signature);
      fd.append('folder', sig.folder);

      const upRes = await fetch(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
        { method: 'POST', body: fd }
      );
      if (!upRes.ok) {
        const errTxt = await upRes.text().catch(() => '');
        throw new Error(errTxt || 'Cloudinary upload failed');
      }
      const result = await upRes.json();
      const url = result.secure_url;
      if (!url) throw new Error('no url returned');

      // 3) Persist the URL on the team record
      await axiosInstance.patch(
        `/v1/events/${eventId}/auction/teams/${team.id}`,
        { team_logo_url: url }
      );
      toast.success('Team logo updated');
      onRefresh?.();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update logo'));
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoRemove = async () => {
    if (!team.team_logo_url) return;
    if (!window.confirm(`Remove logo for ${team.team_name}?`)) return;
    try {
      setUploadingLogo(true);
      await axiosInstance.patch(
        `/v1/events/${eventId}/auction/teams/${team.id}`,
        { team_logo_url: null }
      );
      toast.success('Logo removed');
      onRefresh?.();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to remove logo'));
    } finally {
      setUploadingLogo(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" data-testid={`team-card-${team.id}`}>
      {/* Team Header */}
      <div className="p-4 border-b border-gray-50">
        <div className="flex items-center gap-3">
          <label
            className={`relative w-12 h-12 rounded-lg bg-gradient-to-br from-[#2B3A67] to-[#3AAFA9] flex items-center justify-center overflow-hidden flex-shrink-0 ${isAdmin ? 'cursor-pointer group' : ''}`}
            title={isAdmin ? 'Click to change logo' : undefined}
            data-testid={`team-logo-${team.id}`}
          >
            {team.team_logo_url ? (
              <img src={team.team_logo_url} alt={team.team_name} className="w-full h-full object-cover" />
            ) : (
              <span className="text-white font-bold text-lg">{team.team_name[0]}</span>
            )}
            {isAdmin && (
              <>
                <span className="absolute inset-0 bg-black/0 group-hover:bg-black/45 transition-colors flex items-center justify-center">
                  {uploadingLogo ? (
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                  ) : (
                    <i className="ph-bold ph-camera text-white opacity-0 group-hover:opacity-100 transition-opacity text-base"></i>
                  )}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  disabled={uploadingLogo}
                  className="sr-only"
                  data-testid={`team-logo-input-${team.id}`}
                />
              </>
            )}
          </label>
          <div className="flex-1 min-w-0">
            <h4 className="font-bold truncate">{team.team_name}</h4>
            <p className="text-xs text-gray-500">
              {team.squad_count}/{team.remaining_slots + team.squad_count} players
            </p>
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1 text-gray-400 hover:text-gray-600"
          >
            <i className={`ph-bold ph-caret-${expanded ? 'up' : 'down'}`}></i>
          </button>
        </div>
      </div>

      {/* Wallet Info */}
      <div className="p-4 bg-gradient-to-r from-[#F4D341]/10 to-transparent">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-500 font-medium flex items-center gap-1.5">
              BYC Balance
              {isPostCloseOverride && (
                <span
                  title="Auction is closed — only Super Admin can adjust"
                  className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full uppercase tracking-wider"
                  data-testid={`post-close-tag-${team.id}`}
                >
                  <i className="ph-fill ph-lock-key"></i> Post-close
                </span>
              )}
            </p>
            <p className="text-lg font-bold text-[#2B3A67]">₿ {formatBYC(team.wallet_balance_byc || team.wallet?.balance_byc || 0)}</p>
          </div>
          {canCredit && (
            <div className="flex gap-2">
              <button
                onClick={onCreditWallet}
                className="px-3 py-1.5 bg-[#F4D341] text-[#2B3A67] rounded-lg text-xs font-bold hover:bg-[#e6c73a]"
                data-testid={`credit-wallet-btn-${team.id}`}
              >
                + Credit
              </button>
              <button
                onClick={() => onDebitWallet?.(team)}
                className="px-3 py-1.5 bg-[#E54562]/10 text-[#E54562] rounded-lg text-xs font-bold hover:bg-[#E54562]/20"
                data-testid={`debit-wallet-btn-${team.id}`}
              >
                - Debit
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Owners Section */}
      <div className="p-4 border-t border-gray-50">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs text-gray-500 font-medium">Team Owners ({team.owners?.length || 0})</p>
        </div>
        {team.owners?.length > 0 ? (
          <div className="space-y-2">
            {team.owners.slice(0, expanded ? undefined : 2).map(owner => (
              <div key={owner.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-6 h-6 rounded-full bg-[#2B3A67] flex items-center justify-center">
                    <span className="text-white text-xs font-bold">{owner.user_name?.[0] || '?'}</span>
                  </div>
                  <span className="text-sm truncate">{owner.user_name}</span>
                </div>
              </div>
            ))}
            {!expanded && team.owners.length > 2 && (
              <p className="text-xs text-gray-400 text-center">+{team.owners.length - 2} more</p>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-400">No owners assigned</p>
        )}
      </div>

      {/* Actions */}
      {(canEdit || isSuperAdmin) && expanded && (
        <div className="p-4 border-t border-gray-50 flex flex-col gap-2">
          {team.team_logo_url && isAdmin && (
            <button
              onClick={handleLogoRemove}
              disabled={uploadingLogo}
              className="w-full py-2 px-3 border border-gray-200 text-gray-700 rounded-lg text-xs font-bold hover:bg-gray-50 flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid={`remove-logo-btn-${team.id}`}
              title="Remove this team's logo"
            >
              <i className="ph-bold ph-image-square"></i>
              Remove Logo
            </button>
          )}
          {isSuperAdmin && (
            <button
              onClick={() => setShowRecalc(true)}
              className="w-full py-2 px-3 border border-amber-200 text-amber-700 rounded-lg text-xs font-bold hover:bg-amber-50 flex items-center justify-center gap-2"
              data-testid={`recalc-squad-btn-${team.id}`}
              title="Recompute squad_count and remaining_slots from actual sold players + owners"
            >
              <i className="ph-bold ph-arrows-clockwise"></i>
              Recalculate Squad Count
            </button>
          )}
        </div>
      )}
      {showRecalc && (
        <RecalculateSquadModal
          eventId={eventId}
          team={team}
          squadSize={squadSize}
          onClose={() => setShowRecalc(false)}
          onDone={() => { onRefresh?.(); setShowRecalc(false); }}
        />
      )}
    </div>
  );
}

// Credit Wallet Modal
function CreditWalletModal({ eventId, team, isPostCloseOverride, onClose, onCredited }) {
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const handleCredit = async () => {
    const creditAmount = parseFloat(amount);
    if (!creditAmount || creditAmount <= 0) return;
    if (isPostCloseOverride && !notes.trim()) {
      toast.error('Notes are required for post-close adjustments.');
      return;
    }

    try {
      setSaving(true);
      await axiosInstance.post(`/v1/events/${eventId}/auction/teams/${team.id}/wallet/credit`, {
        amount_byc: creditAmount,
        notes: notes || undefined
      });
      toast.success(`Credited ₿${formatBYC(creditAmount)} to ${team.team_name}`);
      onCredited();
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to credit wallet'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="credit-wallet-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-gray-100 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-bold text-lg">Credit BYC to {team.team_name}</h3>
            <p className="text-sm text-gray-500">Current Balance: ₿ {formatBYC(team.wallet?.balance_byc || 0)}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none -mt-1 -mr-1 flex-shrink-0" aria-label="Close" data-testid="credit-wallet-close">×</button>
        </div>
        {isPostCloseOverride && (
          <div
            data-testid="post-close-override-warning"
            className="mx-5 mt-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 flex items-start gap-2"
          >
            <i className="ph-fill ph-warning-circle text-amber-600 mt-0.5"></i>
            <div className="text-[12px] text-amber-800 leading-snug">
              <strong>Post-close Super Admin adjustment.</strong> The auction is already closed.
              This credit will be tagged as a <code>CREDIT_POST_CLOSE_ADJUSTMENT</code> in the audit ledger.
              <span className="block mt-1">Notes field is required.</span>
            </div>
          </div>
        )}
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount (BYC) *</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter amount"
              min={0}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#3AAFA9] focus:ring-1 focus:ring-[#3AAFA9] outline-none"
              data-testid="credit-amount-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes {isPostCloseOverride ? <span className="text-red-500">*</span> : '(optional)'}
            </label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Reason for credit"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#3AAFA9] focus:ring-1 focus:ring-[#3AAFA9] outline-none"
              data-testid="credit-notes-input"
            />
          </div>
        </div>
        <div className="p-5 border-t border-gray-100 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border border-gray-200 rounded-xl font-bold text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleCredit}
            disabled={saving || !amount || parseFloat(amount) <= 0}
            className="flex-1 py-2.5 bg-[#F4D341] text-[#2B3A67] rounded-xl font-bold hover:bg-[#e6c73a] disabled:opacity-50"
            data-testid="credit-submit"
          >
            {saving ? 'Processing...' : 'Credit Wallet'}
          </button>
        </div>
      </div>
    </div>
  );
}


// Debit Wallet Modal
function DebitWalletModal({ eventId, team, isPostCloseOverride, onClose, onDebited }) {
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const balance = team.wallet_balance_byc || team.wallet?.balance_byc || 0;

  const handleDebit = async () => {
    const debitAmount = parseFloat(amount);
    if (!debitAmount || debitAmount <= 0) return;
    if (debitAmount > balance) {
      toast.error('Debit amount exceeds current balance');
      return;
    }
    if (isPostCloseOverride && !notes.trim()) {
      toast.error('Notes are required for post-close adjustments.');
      return;
    }
    try {
      setSaving(true);
      await axiosInstance.post(`/v1/events/${eventId}/auction/teams/${team.id}/wallet/debit`, {
        amount_byc: debitAmount,
        notes: notes || undefined
      });
      toast.success(`Debited ₿${formatBYC(debitAmount)} from ${team.team_name}`);
      onDebited();
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to debit wallet'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="debit-wallet-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-gray-100 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-bold text-lg">Debit BYC from {team.team_name}</h3>
            <p className="text-sm text-gray-500">Current Balance: ₿ {formatBYC(balance)}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none -mt-1 -mr-1 flex-shrink-0" aria-label="Close" data-testid="debit-wallet-close">×</button>
        </div>
        {isPostCloseOverride && (
          <div
            data-testid="post-close-override-warning-debit"
            className="mx-5 mt-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 flex items-start gap-2"
          >
            <i className="ph-fill ph-warning-circle text-amber-600 mt-0.5"></i>
            <div className="text-[12px] text-amber-800 leading-snug">
              <strong>Post-close Super Admin adjustment.</strong> The auction is already closed.
              This debit will be tagged as a <code>DEBIT_POST_CLOSE_ADJUSTMENT</code> in the audit ledger.
              <span className="block mt-1">Notes field is required.</span>
            </div>
          </div>
        )}
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount (BYC) *</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter debit amount"
              min={0}
              max={balance}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#E54562] focus:ring-1 focus:ring-[#E54562] outline-none"
              data-testid="debit-amount-input"
            />
            {amount && parseFloat(amount) > balance && (
              <p className="text-xs text-red-500 mt-1">Exceeds current balance</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason *</label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Reason for debit"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#E54562] focus:ring-1 focus:ring-[#E54562] outline-none"
              data-testid="debit-notes-input"
            />
          </div>
        </div>
        <div className="p-5 border-t border-gray-100 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border border-gray-200 rounded-xl font-bold text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleDebit}
            disabled={saving || !amount || parseFloat(amount) <= 0 || parseFloat(amount) > balance || !notes}
            className="flex-1 py-2.5 bg-[#E54562] text-white rounded-xl font-bold hover:bg-[#d43a55] disabled:opacity-50"
            data-testid="debit-submit"
          >
            {saving ? 'Processing...' : 'Debit Wallet'}
          </button>
        </div>
      </div>
    </div>
  );
}


// Recalculate Squad Count Modal (Super Admin only)
function RecalculateSquadModal({ eventId, team, squadSize, onClose, onDone }) {
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const handleRecalculate = async () => {
    try {
      setSaving(true);
      const res = await axiosInstance.post(
        `/v1/events/${eventId}/auction/teams/${team.id}/recalculate-squad`
      );
      setResult(res.data);
      if (res.data?.changed) toast.success(res.data.message);
      else toast.info(res.data?.message || 'No change needed');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to recalculate squad'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="recalc-squad-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-gray-100 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-bold text-lg">Recalculate Squad — {team.team_name}</h3>
            <p className="text-xs text-gray-500 mt-1">
              Rebuilds <code>squad_count</code> & <code>remaining_slots</code> from ground truth
              (SOLD pool entries + team owners). Safe & idempotent. Logged to audit trail.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none -mt-1 -mr-1 flex-shrink-0" aria-label="Close" data-testid="recalc-squad-close">×</button>
        </div>

        <div className="p-5 space-y-3">
          <div className="rounded-lg border border-gray-200 p-3">
            <p className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">Stored (now)</p>
            <p className="text-sm mt-1">squad_count: <strong>{team.squad_count}</strong></p>
            <p className="text-sm">remaining_slots: <strong>{team.remaining_slots}</strong></p>
            {squadSize != null && (
              <p className="text-xs text-gray-500 mt-1">Configured squad size: <strong>{squadSize}</strong></p>
            )}
          </div>

          {result && (
            <div className={`rounded-lg border p-3 ${result.changed ? 'border-amber-300 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}>
              <p className="text-[11px] uppercase tracking-wider font-bold text-gray-600">
                {result.changed ? 'Corrected — phantom slot healed' : 'No change — already accurate'}
              </p>
              <p className="text-sm mt-1">
                squad_count:{' '}
                <strong>{result.before.squad_count}</strong>
                {' → '}
                <strong>{result.after.squad_count}</strong>
              </p>
              <p className="text-sm">
                remaining_slots:{' '}
                <strong>{result.before.remaining_slots}</strong>
                {' → '}
                <strong>{result.after.remaining_slots}</strong>
              </p>
              <p className="text-xs text-gray-600 mt-2">
                Ground truth: {result.sold_pool_count} SOLD
                {result.icon_count ? ` + ${result.icon_count} ICON` : ''}
                {result.owner_player_count ? ` + ${result.owner_player_count} OWNER_PLAYER` : ''}
                {' = '}
                {result.sold_pool_count + (result.icon_count || 0) + (result.owner_player_count || 0)} actual squad
                {result.team_owners_count != null && (
                  <span className="block text-[11px] text-gray-500 mt-0.5">
                    ({result.team_owners_count} team owner record{result.team_owners_count === 1 ? '' : 's'} — counted as squad only when owner_will_play and is materialised as OWNER_PLAYER)
                  </span>
                )}
              </p>
            </div>
          )}
        </div>

        <div className="p-5 border-t border-gray-100 flex gap-3">
          <button
            onClick={result ? () => { onDone?.(); } : onClose}
            className="flex-1 py-2.5 border border-gray-200 rounded-xl font-bold text-gray-600 hover:bg-gray-50"
            data-testid="recalc-squad-cancel"
          >
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && (
            <button
              onClick={handleRecalculate}
              disabled={saving}
              className="flex-1 py-2.5 bg-[#2B3A67] text-white rounded-xl font-bold hover:bg-[#1e2a4a] disabled:opacity-50"
              data-testid="recalc-squad-confirm"
            >
              {saving ? 'Recalculating...' : 'Recalculate Now'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
